/**
 * Message composer for Flux Agent: an auto-growing textarea (Enter sends, Shift+Enter inserts a
 * newline), attachment picker and removable chips, a camera button that opens
 * {@link FluxAgentCameraSheet} with removable photo thumbnails, push-to-talk dictation that appends the
 * transcription, a character counter near the server limit, and the send button. The caller owns
 * the draft text so a failed send can keep it.
 *
 * @module apps/web/src/components/flux-agent/FluxAgentComposer
 */

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
  AlertCircle,
  Camera,
  FileText,
  Loader2,
  Mic,
  Paperclip,
  SendHorizontal,
  Square,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { PendingAttachment } from './useFluxAgentAttachments';
import type { PendingPhoto } from './useFluxAgentPhotos';
import { FluxAgentCameraSheet } from './FluxAgentCameraSheet';
import { FluxAgentPhotoThumb } from './FluxAgentMediaViews';
import { useFluxAgentDictation } from './useFluxAgentVoice';
import { UPLOAD_ACCEPT, formatBytes } from '@/lib/fluxAgentFiles';
import { canRecordAudio } from '@/lib/fluxAgentVoice';
import { cn } from '@/lib/utils';

/** Inputs for {@link FluxAgentComposer}. */
export interface FluxAgentComposerProps {
  /** Draft text, owned by the caller. */
  value: string;
  /** Receives every edit, including appended dictation. */
  onChange: (value: string) => void;
  /** Send the current draft and ready attachments. */
  onSubmit: () => void;
  /** True while a reply is pending; typing stays enabled but sending is blocked. */
  pending: boolean;
  /** Disables the whole composer, e.g. while voice mode owns the conversation. */
  disabled?: boolean;
  /** Server limit on message length; 0 disables the counter. */
  maxChars: number;
  /** Byte ceiling for dictation clips. */
  maxAudioBytes: number;
  /** Shows the microphone button when the server can transcribe. */
  canTranscribe: boolean;
  /** Chips for files picked or dropped since the last send. */
  attachments: PendingAttachment[];
  /** Upload newly picked files. */
  onAttach: (files: File[]) => void;
  /** Remove one chip by its client key. */
  onRemoveAttachment: (key: string) => void;
  /** True while any attachment is still uploading; sending waits for it. */
  uploading: boolean;
  /** Photo thumbnails for the next message; omit to hide the camera button. */
  photos?: PendingPhoto[];
  /** Attach prepared photos from the camera sheet. */
  onAddPhotos?: (photos: Blob[]) => void;
  /** Remove one photo thumbnail by its client key. */
  onRemovePhoto?: (key: string) => void;
  /** Most photos one message may carry. Defaults to 4. */
  maxPhotos?: number;
}

/** Render the composer with dictation, attachments, and send controls. */
export function FluxAgentComposer({
  value,
  onChange,
  onSubmit,
  pending,
  disabled = false,
  maxChars,
  maxAudioBytes,
  canTranscribe,
  attachments,
  onAttach,
  onRemoveAttachment,
  uploading,
  photos,
  onAddPhotos,
  onRemovePhoto,
  maxPhotos = 4,
}: FluxAgentComposerProps) {
  const [cameraOpen, setCameraOpen] = useState(false);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const latestValue = useRef(value);
  latestValue.current = value;
  const dictation = useFluxAgentDictation(maxAudioBytes, text => {
    const current = latestValue.current;
    onChange(current && !/\s$/.test(current) ? `${current} ${text}` : `${current}${text}`);
    textarea.current?.focus();
  });

  useEffect(() => {
    const element = textarea.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, 220)}px`;
  }, [value]);

  const overLimit = maxChars > 0 && value.length > maxChars;
  const hasReady =
    attachments.some(item => item.status === 'ready') ||
    !!photos?.some(item => item.status === 'ready');
  const photosLeft = maxPhotos - (photos?.length ?? 0);
  const canSend =
    !pending && !disabled && !uploading && !overLimit && (value.trim().length > 0 || hasReady);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      if (canSend) onSubmit();
    }
  };

  const recording = dictation.state === 'recording';
  const showMic = canTranscribe && canRecordAudio();

  return (
    <div className="space-y-2">
      {photos && photos.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label="Attached photos">
          {photos.map((photo, index) => (
            <li key={photo.key} className="relative" data-testid="flux-agent-pending-photo">
              <FluxAgentPhotoThumb
                src={photo.previewUrl}
                alt={`Photo ${index + 1} to send`}
                className={photo.status === 'error' ? 'ring-destructive opacity-60 ring-2' : undefined}
              />
              {photo.status === 'uploading' && (
                <span className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/30">
                  <Loader2 className="h-4 w-4 animate-spin text-white" aria-label="Uploading photo" />
                </span>
              )}
              {photo.status === 'error' && (
                <span className="sr-only">Upload failed: {photo.error}</span>
              )}
              <button
                type="button"
                onClick={() => onRemovePhoto?.(photo.key)}
                aria-label={`Remove photo ${index + 1}`}
                title={photo.status === 'error' ? photo.error : 'Remove photo'}
                className="bg-background focus-visible:ring-ring absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full border shadow-sm focus-visible:outline-none focus-visible:ring-2"
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {photos?.some(photo => photo.status === 'error') && (
        <p className="text-destructive px-1 text-xs" role="alert">
          {photos.find(photo => photo.status === 'error')?.error}
        </p>
      )}
      {attachments.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label="Attached files">
          {attachments.map(item => (
            <li
              key={item.key}
              className={cn(
                'flex max-w-full items-center gap-1.5 rounded-full border py-1 pl-2.5 pr-1 text-xs',
                item.status === 'error' ? 'border-destructive/40 bg-destructive/5' : 'bg-muted/60'
              )}
            >
              {item.status === 'uploading' ? (
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-label="Uploading" />
              ) : item.status === 'error' ? (
                <AlertCircle className="text-destructive h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              ) : (
                <FileText className="text-primary h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              )}
              <span className="max-w-[12rem] truncate" title={item.name}>
                {item.name}
              </span>
              <span className="text-muted-foreground shrink-0">
                {item.status === 'error' ? item.error : formatBytes(item.sizeBytes)}
              </span>
              <button
                type="button"
                onClick={() => onRemoveAttachment(item.key)}
                aria-label={`Remove ${item.name}`}
                className="hover:bg-background focus-visible:ring-ring flex h-6 w-6 shrink-0 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2"
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div
        className={cn(
          'bg-background focus-within:ring-ring flex items-end gap-1.5 rounded-xl border p-1.5 shadow-sm focus-within:ring-2',
          overLimit && 'border-destructive'
        )}
      >
        <input
          ref={fileInput}
          type="file"
          multiple
          accept={UPLOAD_ACCEPT}
          className="hidden"
          tabIndex={-1}
          onChange={event => {
            const files = Array.from(event.target.files || []);
            if (files.length) onAttach(files);
            event.target.value = '';
          }}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          onClick={() => fileInput.current?.click()}
          disabled={disabled}
          aria-label="Attach files"
          title="Attach files"
        >
          <Paperclip className="h-4 w-4" />
        </Button>
        {onAddPhotos && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={() => setCameraOpen(true)}
            disabled={disabled || photosLeft <= 0}
            aria-label="Take a photo"
            title={photosLeft <= 0 ? `At most ${maxPhotos} photos per message` : 'Take a photo'}
          >
            <Camera className="h-4 w-4" />
          </Button>
        )}
        <label htmlFor="flux-agent-composer" className="sr-only">
          Message Flux Agent
        </label>
        <textarea
          id="flux-agent-composer"
          ref={textarea}
          value={value}
          onChange={event => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
          disabled={disabled}
          placeholder={
            recording
              ? 'Listening… tap stop when you’re done'
              : 'Tell Flux Agent how today is going…'
          }
          aria-describedby="flux-agent-composer-hint"
          className="placeholder:text-muted-foreground max-h-[220px] min-h-[36px] flex-1 resize-none bg-transparent px-1.5 py-2 text-sm outline-none disabled:opacity-60"
        />
        {showMic && (
          <Button
            type="button"
            variant={recording ? 'destructive' : 'ghost'}
            size="icon"
            className="relative h-9 w-9 shrink-0"
            onClick={() => (recording ? dictation.stop() : void dictation.start())}
            disabled={disabled || dictation.state === 'transcribing'}
            aria-label={
              recording
                ? 'Stop dictation'
                : dictation.state === 'transcribing'
                  ? 'Transcribing'
                  : 'Dictate a message'
            }
            aria-pressed={recording}
            title={recording ? 'Stop dictation' : 'Dictate'}
          >
            {dictation.state === 'transcribing' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : recording ? (
              <>
                <span
                  className="bg-destructive/40 absolute inset-0 rounded-md"
                  style={{ transform: `scale(${1 + dictation.level * 0.35})` }}
                  aria-hidden="true"
                />
                <Square className="relative h-3.5 w-3.5 fill-current" />
              </>
            ) : (
              <Mic className="h-4 w-4" />
            )}
          </Button>
        )}
        <Button
          type="button"
          size="icon"
          className="h-9 w-9 shrink-0"
          onClick={onSubmit}
          disabled={!canSend}
          aria-label={pending ? 'Waiting for Flux Agent' : 'Send message'}
          title="Send (Enter)"
        >
          {pending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <SendHorizontal className="h-4 w-4" />
          )}
        </Button>
      </div>

      {onAddPhotos && (
        <FluxAgentCameraSheet
          open={cameraOpen}
          onOpenChange={setCameraOpen}
          onPhotos={onAddPhotos}
          remaining={photosLeft}
        />
      )}

      <div
        id="flux-agent-composer-hint"
        className="text-muted-foreground flex items-center justify-between gap-2 px-1 text-[11px]"
      >
        <span aria-live="polite">
          {dictation.error ? (
            <span className="text-destructive">{dictation.error}</span>
          ) : recording ? (
            'Recording… tap the stop button to transcribe.'
          ) : dictation.state === 'transcribing' ? (
            'Transcribing…'
          ) : uploading ? (
            'Uploading attachments…'
          ) : (
            <span className="hidden sm:inline">
              Enter to send · Shift+Enter for a new line · drop files to attach
            </span>
          )}
        </span>
        {maxChars > 0 && value.length > maxChars * 0.8 && (
          <span
            className={cn('shrink-0 tabular-nums', overLimit && 'text-destructive font-medium')}
          >
            {value.length.toLocaleString()} / {maxChars.toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
}
